import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Alert,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../config/supabase';
import TrademarkFooter from '../common/TrademarkFooter';

export const ResetPasswordForm = () => {
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [email, setEmail] = useState('');
  const [showRecoveryForm, setShowRecoveryForm] = useState(false);
  const [isEmailLoading, setIsEmailLoading] = useState(false);
  const [isEmailSent, setIsEmailSent] = useState(false);

  console.log('ResetPasswordForm component rendering, showRecoveryForm:', showRecoveryForm);

  useEffect(() => {
    const hashParams = new URLSearchParams(window.location.hash.substring(1));
    const errorCode = hashParams.get('error_code');
    
    console.log('Hash params:', hashParams.toString());
    console.log('Error code:', errorCode);

    supabase.auth.getSession().then(({ data }) => {
      console.log('Session data:', data.session);
      
      // Show recovery form if there's no session (regardless of error code)
      // OR if there's specifically an otp_expired error
      if (!data.session || errorCode === 'otp_expired') {
        console.log('Setting showRecoveryForm to true');
        setShowRecoveryForm(true);
      } else {
        console.log('Session exists, keeping regular password reset form');
      }
    });
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitted) return; // Prevent multiple submissions
    
    setError(null);
    setSuccess(null);
    setIsLoading(true);

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      setIsLoading(false);
      return;
    }

    try {
      const { error } = await supabase.auth.updateUser({
        password: password
      });

      if (error) throw error;

      setSuccess('Password has been reset successfully');
      setIsSubmitted(true); // Mark as submitted after success
      // Redirect to login after 2 seconds
      setTimeout(() => {
        navigate('/login');
      }, 2000);
    } catch (err: any) {
      setError(err.message || 'Failed to reset password');
      setIsSubmitted(false); // Reset submission state on error
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendResetEmail = async () => {
    setError(null);
    setSuccess(null);
    setIsEmailLoading(true);

    if (!email) {
      setError('Please enter your email');
      setIsEmailLoading(false);
      return;
    }

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (error) {
        setError(error.message);
      } else {
        setSuccess('We\'ve emailed you a fresh reset link. Please check your inbox.');
        setIsEmailSent(true); // Mark email as sent to disable button permanently
      }
    } catch (err: any) {
      setError(err.message || 'Failed to send reset email');
    } finally {
      setIsEmailLoading(false);
    }
  };

  return (
    <Container component="main" maxWidth="xs">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        {showRecoveryForm && (
          <Paper
            elevation={3}
            sx={{
              padding: 4,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              width: '100%',
              mt: 4,
            }}
          >
            <Typography variant="h6" gutterBottom>
              Link Expired or Already Used
            </Typography>
            <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 2 }}>
              This link is no longer valid. Enter your email below to receive a new password reset link.
            </Typography>

            {error && (
              <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
                {error}
              </Alert>
            )}

            {success && (
              <Alert severity="success" sx={{ width: '100%', mb: 2 }}>
                {success}
              </Alert>
            )}

            <TextField
              fullWidth
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              sx={{ mb: 2 }}
            />
            <Button 
              variant="contained" 
              fullWidth 
              onClick={handleSendResetEmail}
              disabled={isEmailLoading || isEmailSent}
            >
              {isEmailLoading ? 'Sending...' : isEmailSent ? 'Reset Link Sent' : 'Send New Reset Link'}
            </Button>
          </Paper>
        )}

        {!showRecoveryForm && (
          <Paper
            elevation={3}
            sx={{
              padding: 4,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              width: '100%',
            }}
          >
            <Typography component="h1" variant="h5" gutterBottom>
              Set New Password
            </Typography>
            <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
              Please enter your new password.
            </Typography>

            {error && (
              <Alert severity="error" sx={{ width: '100%', mt: 2 }}>
                {error}
              </Alert>
            )}

            {success && (
              <Alert severity="success" sx={{ width: '100%', mt: 2 }}>
                {success}
              </Alert>
            )}

            <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1, width: '100%' }}>
              <TextField
                margin="normal"
                required
                fullWidth
                name="password"
                label="New Password"
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={isSubmitted}
              />
              <TextField
                margin="normal"
                required
                fullWidth
                name="confirmPassword"
                label="Confirm New Password"
                type="password"
                id="confirmPassword"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                disabled={isSubmitted}
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 3, mb: 2 }}
                disabled={isLoading || isSubmitted}
              >
                {isLoading ? 'Resetting...' : isSubmitted ? 'Password Reset' : 'Reset Password'}
              </Button>
            </Box>
          </Paper>
        )}
      </Box>
      <TrademarkFooter />
    </Container>
  );
}; 